import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}
type Category = 'shop-wines' | 'shop-subscriptions' | null;
interface WineRange {
  title: string;
  description: string;
  image: string;
}
const wineRanges: WineRange[] = [{
  title: 'Block',
  description: 'Our everyday collection of carefully crafted wines to express the characteristics or each vintage.',
  image: 'https://images.unsplash.com/photo-1584916201218-f4242ceb4809?auto=format&fit=crop&q=80&w=200&h=600'
}, {
  title: 'Estate',
  description: 'Our signature collection of powerfully individual wines from special sites to intrigue and delight.',
  image: 'https://images.unsplash.com/photo-1559563362-c667ba5f5480?auto=format&fit=crop&q=80&w=200&h=600'
}, {
  title: 'Go',
  description: 'Our unique and rare collection of single-site, terroir expressive wines to enchant, inspire and reward over time.',
  image: 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?auto=format&fit=crop&q=80&w=200&h=600'
}];
const secondaryLinks = ['About', 'Wines', 'Sustainability', 'Contact'];
export function MobileMenu({
  isOpen,
  onClose
}: MobileMenuProps) {
  const [activeCategory, setActiveCategory] = useState<Category>(null);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Staggered entrance animation for wine ranges
  useEffect(() => {
    if (isOpen) {
      setVisibleItems([]);
      const timeouts: NodeJS.Timeout[] = [];
      wineRanges.forEach((_, index) => {
        const timeout = setTimeout(() => {
          setVisibleItems(prev => [...prev, index]);
        }, 300 + index * 200);
        timeouts.push(timeout);
      });
      return () => timeouts.forEach(clearTimeout);
    } else {
      setVisibleItems([]);
    }
  }, [isOpen]);
  return <div className={`fixed inset-0 z-50 bg-[#F5F1E5] transition-opacity duration-500 ease-in-out ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} data-id="element-120">
      <div className="flex h-full w-full" data-id="element-121">
        {/* Left Column - Navigation */}
        <div className="w-full lg:w-1/2 h-full flex flex-col p-6 md:p-12 lg:p-20 relative border-r border-charcoal/10" data-id="element-122">
          {/* Close Button */}
          <button onClick={onClose} className="absolute top-6 left-6 md:top-12 md:left-12 p-2 text-charcoal hover:opacity-70 transition-opacity" aria-label="Close menu" data-id="element-123">
            <X className="w-8 h-8 md:w-10 md:h-10 stroke-[1.5]" data-id="element-124" />
          </button>

          <div className="flex-1 flex flex-col justify-center mt-16 md:mt-0" data-id="element-125">
            {/* Primary Links */}
            <nav className="space-y-4 md:space-y-6 mb-12 md:mb-16" data-id="element-126">
              <div className="group cursor-pointer" onMouseEnter={() => setActiveCategory('shop-wines')} data-id="element-127">
                <a href="#" className="block text-3xl md:text-5xl lg:text-6xl font-optima text-charcoal group-hover:opacity-70 transition-opacity" data-id="element-128">
                  View Wines
                </a>
              </div>
              <div className="group cursor-pointer" onMouseEnter={() => setActiveCategory('shop-subscriptions')} data-id="element-129">
                <a href="#" className="block text-3xl md:text-5xl lg:text-6xl font-optima text-charcoal group-hover:opacity-70 transition-opacity" data-id="element-130">
                  Shop Subscriptions
                </a>
              </div>
            </nav>

            {/* Secondary Links */}
            <nav className="space-y-3 md:space-y-4" data-id="element-131">
              {secondaryLinks.map(link => <a key={link} href="#" className="block text-xs md:text-sm font-semibold uppercase tracking-[0.15em] text-charcoal/80 hover:text-charcoal hover:translate-x-1 transition-all duration-300" onMouseEnter={() => setActiveCategory(null)} data-id="element-132">
                  {link}
                </a>)}
            </nav>
          </div>
        </div>

        {/* Right Column - Dynamic Content (Desktop Only) */}
        <div className="hidden lg:flex w-1/2 h-full bg-[#F5F1E5] flex-col justify-center py-[120px] px-20 overflow-y-auto" data-id="element-133">
          <div className="transition-opacity duration-500 opacity-100" data-id="element-134">
            <div className="space-y-12" data-id="element-135">
              {wineRanges.map((range, index) => <div key={range.title} className="group cursor-pointer transition-all duration-700 ease-out" style={{
              opacity: visibleItems.includes(index) ? 1 : 0,
              transform: visibleItems.includes(index) ? 'translateY(0px)' : 'translateY(24px)'
            }} data-id="element-136">
                  {/* Text Content */}
                  <div className="pt-2" data-id="element-137">
                    <h3 className="text-lg font-optima uppercase tracking-[0.15em] text-charcoal mb-2 group-hover:text-gold transition-colors duration-300" data-id="element-138">
                      {range.title}
                    </h3>
                    <p className="text-charcoal/80 font-optima text-base leading-[1.7] max-w-md" data-id="element-139">
                      {range.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}